import { useMemo, useState } from "react";

import type { FoundryDecisionEngineRecord } from "../api/foundryV2Client";
import { Button } from "./ui";

type Props = {
  decisionEngine: FoundryDecisionEngineRecord | null;
  isLoading: boolean;
  error: string | null;
  isMutating: boolean;
  onRefresh: () => void;
  onDiscuss: (sectionId: string, content: string) => Promise<void> | void;
  onPromote: (sectionId: string, optionId: string) => Promise<void> | void;
};

function statusTone(status: FoundryDecisionEngineRecord["buildSummary"]["status"] | string): string {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "blocked":
      return "border-red-200 bg-red-50 text-red-700";
    case "locked":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "in_discussion":
      return "border-blue-200 bg-blue-50 text-blue-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function criticalityTone(criticality: string): string {
  switch (criticality) {
    case "critical":
      return "border-red-200 bg-red-50 text-red-700";
    case "high":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "medium":
      return "border-blue-200 bg-blue-50 text-blue-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

export function FoundryDecisionChecklistPanel({
  decisionEngine,
  isLoading,
  error,
  isMutating,
  onRefresh,
  onDiscuss,
  onPromote,
}: Props): JSX.Element {
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const [draftMessages, setDraftMessages] = useState<Record<string, string>>({});

  const selectedLookup = useMemo(() => {
    const decisions = decisionEngine?.resolvedDecisions.decisions ?? [];
    return new Map(decisions.map((decision) => [decision.sectionId, decision]));
  }, [decisionEngine]);

  const discussionLookup = useMemo(() => {
    const threads = decisionEngine?.discussions.threads ?? [];
    return new Map(threads.map((thread) => [thread.sectionId, thread]));
  }, [decisionEngine]);

  const cardLookup = useMemo(() => {
    const cards = decisionEngine?.recommendations.cards ?? [];
    return new Map(cards.map((card) => [card.sectionId, card]));
  }, [decisionEngine]);

  const checklistStatusCounts = useMemo(() => {
    const sections = decisionEngine?.checklist.sections ?? [];
    return {
      locked: sections.filter((section) => section.status === "locked").length,
      needsInput: sections.filter((section) => section.status === "needs_input").length,
      inDiscussion: sections.filter((section) => section.status === "in_discussion").length,
    };
  }, [decisionEngine]);

  const sectionStatusLookup = useMemo(() => {
    const sections = decisionEngine?.checklist.sections ?? [];
    return new Map(sections.map((section) => [section.id, section.status]));
  }, [decisionEngine]);

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-decision-checklist-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Foundry decision engine</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">AI recommendations, section by section, before Jarvis executes</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Foundry proposes, explains, and freezes design choices. Jarvis should only execute promoted decisions, not improvise the stack during delivery.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={onRefresh} disabled={isLoading || isMutating}>
            {isLoading ? "Refreshing decisions..." : "Refresh decisions"}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !decisionEngine ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading decision checklist...
        </div>
      ) : null}

      {decisionEngine ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.9fr_0.9fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#eef4ff_100%)] p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(decisionEngine.buildSummary.status)}`}>
                  {decisionEngine.buildSummary.status}
                </span>
                <span className="text-xs text-slate-500">
                  {decisionEngine.buildSummary.criticalLockedCount}/{decisionEngine.buildSummary.criticalTotalCount} critical sections locked
                </span>
              </div>
              <div className="mt-3 text-sm text-slate-700">
                {decisionEngine.buildSummary.blockerLabels.length
                  ? `Build-ready stays blocked until these sections are promoted: ${decisionEngine.buildSummary.blockerLabels.join(", ")}.`
                  : "All critical sections are locked. Remaining sections can still be refined without reopening the design spine."}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 font-semibold text-emerald-700">
                  {checklistStatusCounts.locked} locked
                </span>
                <span className="rounded-full border border-amber-200 bg-white px-2.5 py-1 font-semibold text-amber-700">
                  {checklistStatusCounts.needsInput} need input
                </span>
                <span className="rounded-full border border-blue-200 bg-white px-2.5 py-1 font-semibold text-blue-700">
                  {checklistStatusCounts.inDiscussion} in discussion
                </span>
              </div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {decisionEngine.buildSummary.nextMoves.map((move) => (
                  <li key={move} className="rounded-lg border border-white/70 bg-white/80 px-3 py-2">
                    {move}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Platform profile</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">{decisionEngine.platformProfile.preferredStack}</div>
              <div className="mt-1 text-xs text-slate-600">
                {decisionEngine.platformProfile.developmentMode.replace(/_/g, " ")} to{" "}
                {decisionEngine.platformProfile.productionHostingMode.replace(/_/g, " ")}
              </div>
              <div className="mt-3 space-y-1 text-xs text-slate-600">
                <div>Locality: {decisionEngine.platformProfile.localityStrategy}</div>
                <div>Topology: {decisionEngine.platformProfile.deploymentTopology}</div>
                <div>Residency: {decisionEngine.platformProfile.residencyStrategy}</div>
                <div>Data: {decisionEngine.platformProfile.dataPlatform}</div>
                <div>Auth: {decisionEngine.platformProfile.authStrategy}</div>
                <div>QA: {decisionEngine.platformProfile.qaStrategy}</div>
                <div>Release: {decisionEngine.platformProfile.releaseStrategy}</div>
                <div>Observability: {decisionEngine.platformProfile.observabilityStrategy}</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Domain signals</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">{decisionEngine.domainProfile.title}</div>
              <div className="mt-1 text-xs text-slate-600">{decisionEngine.domainProfile.summary}</div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                <div className="font-semibold text-slate-800">Key questions</div>
                {decisionEngine.domainProfile.keyQuestions.slice(0, 2).map((question) => (
                  <div key={question} className="rounded-lg border border-white bg-white px-3 py-2">
                    {question}
                  </div>
                ))}
                <div className="font-semibold text-slate-800">Proof scenarios</div>
                {decisionEngine.domainProfile.proofScenarios.slice(0, 2).map((scenario) => (
                  <div key={scenario} className="rounded-lg border border-white bg-white px-3 py-2">
                    {scenario}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={decisionEngine.artifactUrls.checklist}>
                  decision_checklist.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={decisionEngine.artifactUrls.recommendations}>
                  recommendation_cards.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={decisionEngine.artifactUrls.resolvedDecisions}>
                  resolved_decisions.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={decisionEngine.artifactUrls.domainProfile}>
                  domain_profile.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={decisionEngine.artifactUrls.platformProfile}>
                  platform_profile.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            {decisionEngine.checklist.sections.map((section) => {
              const card = cardLookup.get(section.id);
              const selected = selectedLookup.get(section.id);
              const discussion = discussionLookup.get(section.id);
              const isExpanded = expandedSectionId === section.id;
              const discussionDraft = draftMessages[section.id] ?? "";

              return (
                <article
                  key={section.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)]"
                  data-testid={`decision-section-${section.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-base font-semibold text-slate-900">{section.title}</h4>
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${criticalityTone(section.criticality)}`}>
                          {section.criticality}
                        </span>
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusTone(section.status)}`}>
                          {section.status}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-slate-600">
                        {section.rationalePreview.join(" ") || "No rationale preview yet."}
                      </div>
                      {section.dependsOn.length ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {section.dependsOn.map((dependencyId) => {
                            const dependencyStatus = sectionStatusLookup.get(dependencyId);
                            return (
                              <span
                                key={dependencyId}
                                className={`rounded-full border px-2 py-0.5 text-[11px] ${
                                  dependencyStatus === "locked"
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-slate-50 text-slate-600"
                                }`}
                              >
                                Depends on {dependencyId.replace(/_/g, " ")}
                              </span>
                            );
                          })}
                        </div>
                      ) : null}
                      {selected ? (
                        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                          Locked decision: <span className="font-semibold">{selected.label}</span>
                          <div className="mt-1 text-xs text-emerald-700">{selected.summary}</div>
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                      onClick={() => setExpandedSectionId(isExpanded ? null : section.id)}
                    >
                      {isExpanded ? "Collapse" : "Open"}
                    </button>
                  </div>

                  {card ? (
                    <div className="mt-4 rounded-2xl border border-blue-200 bg-[linear-gradient(135deg,#eff6ff_0%,#f8fafc_100%)] p-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-slate-900">
                          Recommended: {card.options.find((option) => option.id === card.recommendedOptionId)?.label ?? "Option"}
                        </div>
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-blue-200 bg-white text-[11px] font-semibold text-blue-700"
                          title={[...card.rationale, ...card.tradeoffs, ...card.assumptions].join("\n")}
                          aria-label={`Why Foundry recommends this choice for ${section.title}`}
                        >
                          ?
                        </button>
                        <span className="text-xs text-slate-500">{(card.confidence * 100).toFixed(0)}% confidence</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {card.options
                          .find((option) => option.id === card.recommendedOptionId)
                          ?.stack.map((entry) => (
                            <span key={entry} className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[11px] text-blue-700">
                              {entry}
                            </span>
                          ))}
                      </div>
                      <div className="mt-3 text-xs text-slate-600">{card.rationale.join(" ")}</div>
                    </div>
                  ) : null}

                  {isExpanded && card ? (
                    <div className="mt-4 space-y-4">
                      <div className="grid gap-3 xl:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Why this recommendation</div>
                          <ul className="mt-3 space-y-2 text-xs text-slate-600">
                            {card.rationale.map((entry) => (
                              <li key={entry} className="rounded-lg border border-white bg-white px-3 py-2">
                                {entry}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tradeoffs</div>
                          <ul className="mt-3 space-y-2 text-xs text-slate-600">
                            {card.tradeoffs.map((entry) => (
                              <li key={entry} className="rounded-lg border border-white bg-white px-3 py-2">
                                {entry}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Assumptions</div>
                          <ul className="mt-3 space-y-2 text-xs text-slate-600">
                            {card.assumptions.map((entry) => (
                              <li key={entry} className="rounded-lg border border-white bg-white px-3 py-2">
                                {entry}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="grid gap-3">
                        {card.options.map((option) => {
                          const isRecommended = option.id === card.recommendedOptionId;
                          const isLockedOption = selected?.optionId === option.id;
                          return (
                            <div
                              key={option.id}
                              className={`rounded-2xl border p-4 ${
                                isLockedOption
                                  ? "border-emerald-200 bg-emerald-50"
                                  : isRecommended
                                    ? "border-blue-200 bg-blue-50"
                                    : "border-slate-200 bg-slate-50"
                              }`}
                            >
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div className="text-sm font-semibold text-slate-900">{option.label}</div>
                                    {isRecommended ? (
                                      <span className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                                        AI recommended
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="mt-1 text-sm text-slate-600">{option.description}</div>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {option.stack.map((entry) => (
                                      <span
                                        key={entry}
                                        className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-700"
                                      >
                                        {entry}
                                      </span>
                                    ))}
                                  </div>
                                  {option.suitableWhen.length ? (
                                    <div className="mt-3 text-xs text-slate-600">
                                      Suitable when: {option.suitableWhen.join(" ")}
                                    </div>
                                  ) : null}
                                  {option.caution.length ? (
                                    <div className="mt-1 text-xs text-amber-700">Caution: {option.caution.join(" ")}</div>
                                  ) : null}
                                </div>
                                <Button
                                  variant={isLockedOption ? "secondary" : "primary"}
                                  size="sm"
                                  onClick={() => void onPromote(section.id, option.id)}
                                  disabled={isMutating || isLockedOption}
                                >
                                  {isLockedOption ? "Locked" : "Promote"}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Discuss this section</div>
                        <textarea
                          value={discussionDraft}
                          onInput={(event) =>
                            setDraftMessages((current) => ({
                              ...current,
                              [section.id]: (event.target as HTMLTextAreaElement).value,
                            }))
                          }
                          className="mt-3 min-h-[92px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                          placeholder={`Example: for ${section.title}, explain the constraint or ask Foundry to justify another stack.`}
                        />
                        <div className="mt-3 flex justify-end">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const value = discussionDraft.trim();
                              if (!value) return;
                              void onDiscuss(section.id, value);
                              setDraftMessages((current) => ({ ...current, [section.id]: "" }));
                            }}
                            disabled={isMutating || !discussionDraft.trim()}
                          >
                            Discuss with AI
                          </Button>
                        </div>
                        {discussion?.messages?.length ? (
                          <div className="mt-4 space-y-2">
                            {discussion.messages.slice(-3).map((message) => (
                              <div key={message.id} className="rounded-xl border border-white bg-white px-3 py-2 text-sm text-slate-700">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{message.author}</div>
                                <div className="mt-1 whitespace-pre-wrap">{message.content}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );
}
